import {Button, CircularProgress} from '@mui/material';
import {DateTime} from 'luxon';
import {useOptimisticSearchParams} from 'nuqs/adapters/react-router/v7';
import {useEffect, useState} from 'react';
import {useNavigate} from 'react-router';
import {
  SearchQuery,
  useGetCatalogSearch,
  useGetCollection,
} from '@src/api/catalog';
import {useGetConfigurationCollections} from '@src/api/configurations';
import {notifyError} from '@src/components/notifier/notify';
import {WORLD_EXTENT} from '@src/constants';
import {ANALYZE_VISUALIZE_ROUTE} from '@src/constants/routes';
import useVisualizationParams from '@src/hooks/useVisualizationParams';
import dimensions from '@src/styles/dimensions';
import {AppLocationState} from '@src/types/appLocation';
import {Collection} from '@src/types/configurations';
import {parseDateTimeFromCollectionWithDefaults} from '@src/utils/collections';

const getLastMonthTimeRangeFromCollection = (
  collection: Collection,
): string => {
  const {dateTimeTo} = parseDateTimeFromCollectionWithDefaults(collection);
  return `${dateTimeTo.minus({months: 1}).toISO()}/${dateTimeTo.toISO()}`;
};

const parseExtentBboxFromCollection = (collection: Collection) => {
  return collection?.extent?.spatial?.bbox?.[0] ?? WORLD_EXTENT;
};

export const VisualizeButton = () => {
  const navigate = useNavigate();
  const {
    collectionId,
    configurationId,
    serialize: serializeVisualizationParams,
  } = useVisualizationParams();
  const {collections} = useGetConfigurationCollections(configurationId);
  const selectedCollectionId = collectionId ?? collections[0]?.id;
  const searchParams = useOptimisticSearchParams();
  const {data: collection} = useGetCollection(selectedCollectionId);

  const [searchQueryParams, setSearchQueryParams] = useState<
    SearchQuery | undefined
  >();

  const {data: searchResults, isLoading: isLoadingResults} =
    useGetCatalogSearch(searchQueryParams!, {enabled: !!searchQueryParams});

  const {bbox: firstResultBBox, properties} =
    searchResults?.features?.[0] || {};
  const firstResultDate = properties?.datetime;
  if (firstResultDate) {
    const dateTimeFrom = DateTime.fromISO(firstResultDate)
      .startOf('day')
      .toISO();
    const dateTimeTo = DateTime.fromISO(firstResultDate).endOf('day').toISO();
    searchParams.set('from', dateTimeFrom ?? '');
    searchParams.set('to', dateTimeTo ?? '');
  }
  useEffect(() => {
    if (searchResults && firstResultBBox?.length === 4) {
      try {
        const defaultVisualizeParameters = new URLSearchParams(
          serializeVisualizationParams({
            collectionId: selectedCollectionId,
            configurationId,
            layerId: '',
          }),
        );
        const navigateParams = new URLSearchParams({
          ...Object.fromEntries(defaultVisualizeParameters.entries()),
          ...Object.fromEntries(searchParams.entries()),
        });
        navigate(
          {
            pathname: `/${ANALYZE_VISUALIZE_ROUTE}`,
            search: navigateParams.toString(),
          },
          {
            state: {
              mapExtent: {
                bbox: firstResultBBox,
                duration: 300,
              },
              tileBbox: firstResultBBox,
            } satisfies AppLocationState,
          },
        );
      } catch (error) {
        notifyError({
          data: {
            message: 'Error fetching catalog data',
          },
        });
      } finally {
        setSearchQueryParams(undefined);
      }
    }
  }, [
    firstResultBBox,
    navigate,
    searchResults,
    searchParams,
    serializeVisualizationParams,
    selectedCollectionId,
    configurationId,
  ]);

  const isDisabled =
    !configurationId || !selectedCollectionId || isLoadingResults;
  return (
    <Button
      onClick={() => {
        if (!collection) {
          return;
        }
        setSearchQueryParams({
          bbox: parseExtentBboxFromCollection(collection),
          datetime: getLastMonthTimeRangeFromCollection(collection),
          collections: [collection.id],
          limit: 10,
        });
      }}
      disabled={isDisabled}
      variant="contained"
      color="primary"
      size="large"
      sx={{
        marginBlockStart: 0.25,
        marginBlockEnd: 1,
        display: 'flex',
        justifyContent: 'center',
        marginInline: `${dimensions.leftSidebar.contentMargin}px`,
      }}
    >
      {isLoadingResults ? (
        <CircularProgress size={24} sx={{marginBlock: 0.15}} />
      ) : (
        'Visualize'
      )}
    </Button>
  );
};

export default VisualizeButton;
